import React, { useMemo, useRef } from "react";
import { Box, Divider, Paper, Typography, useTheme } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { compactMoney, money } from "./format";
import { formatDay } from "./cashTimeline";

const MARGIN = { top: 16, right: 20, bottom: 64, left: 72 };
const LEGEND = {
  direction: "row",
  position: { vertical: "bottom", horizontal: "middle" },
  padding: 0,
  itemMarkHeight: 8,
  labelStyle: { fontSize: 12 },
};

const hours = (value) => (value == null ? "" : `${Math.round(value).toLocaleString("en-US")} h`);

/**
 * One chart on the shared day axis, with the cursor and "today" drawn in, and
 * pointer-drag on the plot moving the cursor. (x-charts v6 has no axis-click
 * event, so the day is worked out from the pointer against the plot margins.)
 */
function TimelineChart({ title, subtitle, height, timeline, cursorDay, onCursorChange, xData, series, yFormatter, sx, children }) {
  const { palette } = useTheme();
  const boxRef = useRef(null);

  const moveTo = (event) => {
    const rect = boxRef.current.getBoundingClientRect();
    const plotWidth = rect.width - MARGIN.left - MARGIN.right;
    const ratio = (event.clientX - rect.left - MARGIN.left) / plotWidth;
    onCursorChange(Math.min(timeline.days, Math.max(0, Math.round(ratio * timeline.days))));
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ px: 1, pt: 1 }}>
        <Typography variant="subtitle2">
          {title}{" "}
          {subtitle && (
            <Typography component="span" variant="caption" color="text.secondary">
              · {subtitle}
            </Typography>
          )}
        </Typography>
      </Box>
      <Box
        ref={boxRef}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          moveTo(event);
        }}
        onPointerMove={(event) => {
          if (event.buttons) moveTo(event);
        }}
        sx={{ cursor: "ew-resize", touchAction: "none", userSelect: "none" }}
      >
        <LineChart
          height={height}
          margin={MARGIN}
          xAxis={[
            {
              scaleType: "linear",
              data: xData ?? timeline.dayIndex,
              min: 0,
              max: timeline.days,
              valueFormatter: (day) => formatDay(timeline, day),
            },
          ]}
          yAxis={[{ valueFormatter: yFormatter }]}
          series={series}
          slotProps={{ legend: LEGEND }}
          sx={sx}
        >
          {children}
          <ChartsReferenceLine
            x={timeline.todayDay}
            label="Today"
            labelAlign="start"
            lineStyle={{ stroke: palette.text.disabled, strokeDasharray: "2 4" }}
            labelStyle={{ fontSize: 10, fill: palette.text.disabled }}
          />
          <ChartsReferenceLine x={cursorDay} lineStyle={{ stroke: palette.text.primary, strokeWidth: 1.25 }} />
        </LineChart>
      </Box>
    </Box>
  );
}

const moneyLine = (id, label, data, color, extra = {}) => ({
  id,
  label,
  data,
  color,
  curve: "stepAfter",
  showMark: false,
  valueFormatter: money,
  ...extra,
});

/**
 * The cash timeline as five charts on one day axis:
 *  1. money, cumulative - billed and collected from the client against what
 *     has gone out to vendors and payroll (stacked);
 *  2. the cash cushion - collected minus paid out;
 *  3-4. the two lanes, client -> Chinook -> material vendors / payroll;
 *  5. people - man-hours per week by discipline.
 * The selected line's events are marked on the first chart.
 */
export default function CashTimelineCharts({ timeline, cursorDay, onCursorChange, selectedCode }) {
  const { palette } = useTheme();
  const { series, totals } = timeline;

  const markers = useMemo(() => {
    if (!selectedCode) return [];
    const colorOf = {
      bill: palette.text.secondary,
      collect: palette.success.main,
      vendor: palette.warning.main,
      payroll: palette.secondary.main,
    };
    const seen = new Set();
    return timeline.events
      .filter((event) => event.code === selectedCode)
      .filter((event) => {
        const key = `${event.type}:${event.day}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((event) => ({ key: `${event.type}:${event.day}`, day: event.day, color: colorOf[event.type] }));
  }, [selectedCode, timeline.events, palette]);

  const everFunding = series.cushion.some((value) => value < -1);
  const hourColors = [palette.info.light, palette.info.main, palette.info.dark, palette.grey[500]];
  const shared = { timeline, cursorDay, onCursorChange };

  return (
    <Paper variant="outlined" sx={{ py: 1 }}>
      <TimelineChart
        {...shared}
        title="Money, cumulative"
        subtitle="client → Chinook → out"
        height={300}
        yFormatter={compactMoney}
        series={[
          moneyLine("vendorPaid", "Vendors paid", series.vendorPaid, palette.warning.main, { stack: "out", area: true }),
          moneyLine("payrollPaid", "Payroll paid", series.payrollPaid, palette.secondary.main, { stack: "out", area: true }),
          moneyLine("billed", "Billed (switch = 1)", series.billed, palette.success.light),
          moneyLine("collected", "Collected from client", series.collected, palette.success.main),
        ]}
        sx={{
          "& .MuiAreaElement-root": { fillOpacity: 0.55 },
          "& .MuiLineElement-series-billed": { strokeDasharray: "4 3" },
          "& .MuiLineElement-series-collected": { strokeWidth: 2.5 },
        }}
      >
        <ChartsReferenceLine
          y={totals.contract}
          label={`Contract ${money(totals.contract)}`}
          labelAlign="end"
          lineStyle={{ stroke: palette.text.disabled, strokeDasharray: "1 3" }}
          labelStyle={{ fontSize: 10, fill: palette.text.secondary }}
        />
        {markers.map((marker) => (
          <ChartsReferenceLine
            key={marker.key}
            x={marker.day}
            lineStyle={{ stroke: marker.color, strokeDasharray: "2 3", strokeWidth: 1.5 }}
          />
        ))}
      </TimelineChart>

      <TimelineChart
        {...shared}
        title="Cash cushion"
        subtitle={everFunding ? "collected minus paid out — red: Chinook funding the client" : "collected minus paid out"}
        height={200}
        yFormatter={compactMoney}
        series={[
          moneyLine("cushion", "Chinook holding", series.cushion, everFunding ? palette.error.main : palette.primary.main, {
            area: true,
          }),
        ]}
        sx={{ "& .MuiAreaElement-root": { fillOpacity: 0.3 } }}
      >
        <ChartsReferenceLine y={0} lineStyle={{ stroke: palette.text.disabled }} />
      </TimelineChart>

      <Divider sx={{ my: 1 }} />

      <TimelineChart
        {...shared}
        title="Lane 1"
        subtitle="client → Chinook → material vendors & subcontractors"
        height={200}
        yFormatter={compactMoney}
        series={[
          moneyLine("laneVendorPaid", "Paid to vendors", series.vendorPaid, palette.warning.main, { area: true }),
          moneyLine("laneVendorIn", "Collected for material", series.collectedVendor, palette.success.main),
        ]}
        sx={{ "& .MuiAreaElement-root": { fillOpacity: 0.45 } }}
      />

      <TimelineChart
        {...shared}
        title="Lane 2"
        subtitle="client → Chinook → payroll"
        height={200}
        yFormatter={compactMoney}
        series={[
          moneyLine("lanePayrollPaid", "Payroll paid", series.payrollPaid, palette.secondary.main, { area: true }),
          moneyLine("lanePayrollIn", "Collected for labour", series.collectedPayroll, palette.success.main),
        ]}
        sx={{ "& .MuiAreaElement-root": { fillOpacity: 0.45 } }}
      />

      <Divider sx={{ my: 1 }} />

      {timeline.disciplines.length === 0 ? (
        <Box sx={{ px: 1, py: 3 }}>
          <Typography variant="subtitle2">People</Typography>
          <Typography variant="body2" color="text.secondary">
            No man-hours to show — a line needs a matched task with dates, and
            hours on the task (or a man-hour estimate).
          </Typography>
        </Box>
      ) : (
        <TimelineChart
          {...shared}
          title="People"
          subtitle="man-hours per week"
          height={220}
          yFormatter={hours}
          xData={timeline.weeks.map((week) => week.startDay)}
          series={timeline.disciplines.map((name, i) => ({
            id: `hours-${i}`,
            label: name,
            data: timeline.weeks.map((week) => week.hours[name]),
            color: hourColors[i % hourColors.length],
            stack: "hours",
            area: true,
            curve: "stepAfter",
            showMark: false,
            valueFormatter: hours,
          }))}
          sx={{ "& .MuiAreaElement-root": { fillOpacity: 0.85 } }}
        />
      )}
    </Paper>
  );
}
